import { create } from 'zustand';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'cookie-consent';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentState {
  hasConsent: boolean;
  preferences: CookiePreferences;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustomPreferences: (prefs: Partial<CookiePreferences>) => void;
  resetConsent: () => void;
  isConsented: (category: keyof CookiePreferences) => boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

function loadFromStorage(): { hasConsent: boolean; preferences: CookiePreferences } {
  if (typeof window === 'undefined') {
    return { hasConsent: false, preferences: DEFAULT_PREFERENCES };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        hasConsent: parsed.hasConsent ?? false,
        preferences: {
          necessary: true,
          analytics: parsed.preferences?.analytics ?? false,
          marketing: parsed.preferences?.marketing ?? false,
        },
      };
    }
  } catch (error) {
    logger.error('Failed to load cookie consent from localStorage', error, {
      feature: 'cookie-consent-store',
      route: 'loadFromStorage',
    });
  }
  return { hasConsent: false, preferences: DEFAULT_PREFERENCES };
}

function persistToStorage(hasConsent: boolean, preferences: CookiePreferences) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ hasConsent, preferences, timestamp: Date.now() })
    );
  } catch (error) {
    logger.error('Failed to persist cookie consent to localStorage', error, {
      feature: 'cookie-consent-store',
      route: 'persistToStorage',
    });
  }
}

/**
 * Pushes the latest consent state into Google Consent Mode v2. No-ops if
 * `gtag` hasn't been initialized yet (e.g., user rejects on first paint
 * before any tracker loads — the denied defaults set by `ConsentDefaults`
 * already cover that case).
 */
function updateGtagConsent(prefs: CookiePreferences) {
  if (typeof window === 'undefined') return;
  const w = window as Window & {
    gtag?: (command: 'event' | 'config' | 'set' | 'consent', ...args: unknown[]) => void;
  };
  if (typeof w.gtag !== 'function') return;
  try {
    w.gtag('consent', 'update', {
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
    });
  } catch (error) {
    logger.warn('Failed to update gtag consent', {
      feature: 'cookie-consent-store',
      route: 'updateGtagConsent',
      error,
    });
  }
}

/**
 * Clears Google Analytics cookies when analytics consent is revoked.
 */
function clearAnalyticsCookies() {
  if (typeof document === 'undefined') return;

  const cookiesToClear = ['_ga', '_gid', '_gat', '_gac_', '_hjid', '_hjSession', '_hjSessionUser'];
  const hostname = window.location.hostname;
  const domains = [hostname, `.${hostname}`, ''];

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    const shouldClear = cookiesToClear.some((prefix) => name.startsWith(prefix));
    if (shouldClear) {
      domains.forEach((domain) => {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`;
      });
    }
  });
}

const initial = loadFromStorage();

export const useCookieConsentStore = create<CookieConsentState>((set, get) => ({
  hasConsent: initial.hasConsent,
  preferences: initial.preferences,

  acceptAll: () => {
    const preferences: CookiePreferences = { necessary: true, analytics: true, marketing: true };
    set({ hasConsent: true, preferences });
    persistToStorage(true, preferences);
    updateGtagConsent(preferences);
  },

  rejectAll: () => {
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    set({ hasConsent: true, preferences });
    persistToStorage(true, preferences);
    updateGtagConsent(preferences);
    clearAnalyticsCookies();
  },

  saveCustomPreferences: (prefs: Partial<CookiePreferences>) => {
    const current = get().preferences;
    const preferences: CookiePreferences = {
      necessary: true,
      analytics: prefs.analytics ?? current.analytics,
      marketing: prefs.marketing ?? current.marketing,
    };
    const wasAnalyticsEnabled = current.analytics;
    set({ hasConsent: true, preferences });
    persistToStorage(true, preferences);
    updateGtagConsent(preferences);
    if (wasAnalyticsEnabled && !preferences.analytics) {
      clearAnalyticsCookies();
    }
  },

  resetConsent: () => {
    set({ hasConsent: false, preferences: DEFAULT_PREFERENCES });
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    updateGtagConsent(DEFAULT_PREFERENCES);
    clearAnalyticsCookies();
  },

  isConsented: (category: keyof CookiePreferences) => {
    return get().preferences[category];
  },
}));
